import * as React from 'react';

import { mapKeys } from 'lodash/fp';

import {
    CASE_HOSPITALIZATION_ID_KEY,
    CASE_OUTCOME_ID_KEY,
    CASE_SEVERITY_ID_KEY,
    INFECTION_TYPE_ID_KEY,
    PAYER_ID_KEY,
    SEROTYPE_ID_KEY,
    TOC_ID_KEY,
    VACCINATION_STATUS_ID_KEY,
    VACCINE_STATUS_ID_KEY,
    VEHSS_CONDITION_ID_KEY,
    WOLBACHIA_INCLUDED_ID_KEY,
    YEAR_START_KEY,
} from '../../models/data-key';
import {
    AntimicrobialResistanceRecords,
    CauseAssociatedComorbidityStudyCoefficients,
    CauseAssociatedCoprevalenceRecords,
    CauseCoprevalenceRecords,
    CauseOutcome,
    CovariateRecords,
    Covid19Records,
    EtiologyRecords,
    ExpenditureRecords,
    ImpairmentRecords,
    InjuryRecords,
    LifeExpectancyRecords,
    MultipleMyelomaOutputShell,
    MultipleMyelomaSurvivalResults,
    PopulationRecords,
    ProjectedComorbidityCausesOutcomeRecords,
    RaceEthnicityEyeConditionsRecords,
    RiskAttribution,
    RiskExposureRecords,
    RiskPrevalence,
    SdohRecords,
    SequelaOutcome,
    SevRecords,
    TakedaDengueRecords,
} from '../../models/data-type';
import en from '../en';
import ageGroups from './age-groups';
import antibioticClasses from './antibiotic-classes';
import caseHospitalizations from './case-hospitalizations';
import caseOutcomes from './case-outcomes';
import caseSeverities from './case-severities';
import causes from './causes';
import counterfactuals from './counterfactuals';
import covariates from './covariates';
import dataTypes from './data-types';
import educations from './educations';
import employmentCohorts from './employment-cohorts';
import employmentStatuses from './employment-statuses';
import ethnicities from './ethnicities';
import facilities from './facilities';
import forecastScenarios from './forecast-scenarios';
import genders from './genders';
import ght from './global-health-metrics';
import indicatorGroups from './indicator-groups';
import indicators from './indicators';
import industries from './industries';
import infectionTypes from './infection-types';
import infectiousSyndromes from './infectious-syndromes';
import injuries from './injuries';
import locations from './locations';
import measures from './measures';
import metrics from './metrics';
import multipleMyelomaLineOfTreatment from './multiple-myeloma-line-of-treatment';
import multipleMyelomaPopulation from './multiple-myeloma-population';
import multipleMyelomaTreatments from './multiple-myeloma-treatments';
import organizations from './organizations';
import pathogens from './pathogens';
import payers from './payers';
import races from './races';
import rei from './rei';
import responses from './responses';
import riskExposures from './risk-exposures';
import sdohs from './sdohs';
import sequelas from './sequelas';
import serotypes from './serotypes';
import severityOutcomes from './severity-outcomes';
import tocs from './tocs';
import vaccinationStatuses from './vaccination-statuses';
import vehssConditions from './vehss-conditions';
import wolbachiaIncluded from './wolbachia-included';

export default {
    ...en,
    ...causes,
    ...sequelas,
    ...rei,
    ...injuries,
    ...covariates,
    ...sdohs,
    ...ageGroups,
    ...locations,
    ...measures,
    ...metrics,
    ...forecastScenarios,
    ...dataTypes,
    ...riskExposures,
    ...indicators,
    ...indicatorGroups,
    ...responses,
    ...industries,
    ...organizations,
    ...employmentCohorts,
    ...employmentStatuses,
    ...facilities,
    ...genders,
    ...ethnicities,
    ...races,
    ...pathogens,
    ...antibioticClasses,
    ...counterfactuals,
    ...infectiousSyndromes,
    ...multipleMyelomaPopulation,
    ...multipleMyelomaLineOfTreatment,
    ...multipleMyelomaTreatments,
    ...educations,
    ...ght,
    ...vehssConditions,
    ...severityOutcomes,
    ...caseOutcomes,
    ...caseHospitalizations,
    ...caseSeverities,
    ...infectionTypes,
    ...serotypes,
    ...vaccinationStatuses,
    ...wolbachiaIncluded,
    ...payers,
    ...tocs,

    // comorbid causes dictionary
    ...mapKeys((key) => 'comorbid_' + key, causes),

    // Common Terms
    yes_label: 'Yes',
    no_label: 'No',
    all_label: 'All',
    ok_button: 'Ok',
    save_button: 'Save',
    cancel_button: 'Cancel',
    delete_button: 'Confirm',
    back_button: 'Back',
    next_button: 'Next',
    reset_button: 'Reset',
    submit_button: 'Submit',
    download_button: 'Download',
    show_later_button: 'Show Later',

    search_placeholder: 'Search...',
    select_placeholder: 'Select...',
    loading_placeholder: 'Loading...',
    empty_label_default_text: 'empty',
    password_requirements: 'Password should be at least 8 characters long',
    saved_successfully: 'Saved successfully!',
    learn_more: 'Learn More',
    clear: 'Clear',
    ids: 'ids',
    comma_separated_ids: 'Comma-separated Ids',
    add_ids: 'Add Ids',
    select_all: 'Select All',
    multiselect_label: 'Hold shift and click to select a range',
    data_table_rows_per_page: 'Rows per page:',

    all: 'All',
    causes: 'Causes',
    sequelas: 'Sequelas',
    risks: 'Risks',
    etiologies: 'Etiologies',
    impairments: 'Impairments',
    sevs: 'SEV',
    injuries: 'Injuries',
    covariates: 'Covariates',
    sdohs: 'SDoH',
    riskExposures: 'Risk Exposures',
    population: 'Population',
    covid19: 'Covid-19',
    lifeExpectancy: 'Life Expectancy',
    antimicrobialResistance: 'Antimicrobial Resistance',
    multipleMyelomaOutputShell: 'Multiple Myeloma Output Shell',
    multipleMyelomaPopulation: 'Multiple Myeloma Population',
    multipleMyelomaSurvivalResults: 'Multiple Myeloma Survival Results',
    healthThreatsMetricsRecords: 'Health Threats',
    raceEthnicityEyeConditionsRecords: 'Eye Conditions',
    expenditureRecords: 'Expenditure',

    cause: 'Cause',
    sequela: 'Sequela',
    risk: 'Risk Burden',
    etiology: 'Etiology',
    impairment: 'Impairment',
    injury: 'Injury',
    sev: 'SEV',
    riskexposure: 'Risk Exposure',
    riskprevalence: 'Risk Prevalence',
    covariate: 'Covariate',
    sdoh: 'SDoH',

    [AntimicrobialResistanceRecords]: 'Antimicrobial Resistance',
    [CauseAssociatedComorbidityStudyCoefficients]: 'Associated Projected New Cases',
    [CauseAssociatedCoprevalenceRecords]: 'Associated Current Co-Prevalence',
    [CauseCoprevalenceRecords]: 'Current Co-Prevalence',
    [CauseOutcome]: 'Cause',
    [CovariateRecords]: 'Covariate',
    [Covid19Records]: 'Covid-19',
    [EtiologyRecords]: 'Etiology',
    [ExpenditureRecords]: 'Expenditure',
    [ImpairmentRecords]: 'Impairment',
    [InjuryRecords]: 'Injury',
    [LifeExpectancyRecords]: 'Life Expectancy',
    [MultipleMyelomaOutputShell]: 'Multiple myeloma output shell',
    [MultipleMyelomaSurvivalResults]: 'Multiple myeloma survival results',
    [PopulationRecords]: 'Population',
    [ProjectedComorbidityCausesOutcomeRecords]: 'Projected New Cases',
    [RaceEthnicityEyeConditionsRecords]: 'Eye Conditions',
    [RiskAttribution]: 'Risk Burden',
    [RiskExposureRecords]: 'Risk Exposure',
    [RiskPrevalence]: 'Risk Prevalence',
    [SdohRecords]: 'SDoH',
    [SequelaOutcome]: 'Sequela',
    [SevRecords]: 'SEV',
    [TakedaDengueRecords]: 'Dengue',

    search_year: 'Search year...',
    search_round_id: 'Search data suite...',
    search_gender_id: 'Search sex...',
    search_age_group_id: 'Search age...',
    search_forecast_scenario_id: 'Search scenario...',
    search_cause_id: 'Search cause...',
    search_metric_id: 'Search unit...',
    search_measure_id: 'Search measure...',
    search_location_id: 'Search location...',
    search_sequela_id: 'Search sequela...',
    search_risk_attribution: 'Search risk...',
    search_risk_prevalence: 'Search risk...',
    search_etiology_records: 'Search etiology...',
    search_impairment_records: 'Search impairment...',
    search_injury_records: 'Search injury...',
    search_sev_records: 'Search sev...',
    search_covariate_id: 'Search covariate...',
    search_sdoh_id: 'Search SDoH...',
    search_day: 'Search day...',
    search_comorbid_cause_id: 'Search comorbid condition...',
    search_month_count: 'Search duration (months)...',
    search_rei_id: 'Search risk...',
    search_risk_exposure_id: 'Search exposure...',
    search_race_id: 'Search race...',
    search_facility_id: 'Search facility...',
    search_industry_id: 'Search industry...',
    search_response_id: 'Search response...',
    search_ethnicity_id: 'Search ethnicity...',
    search_indicator_id: 'Search indicator...',
    search_employment_cohort_id: 'Search employment cohort...',
    search_employment_status_id: 'Search employment status...',
    search_portal_organization_id: 'Search organization...',
    search_pathogen_id: 'Search pathogen...',
    search_antibiotic_class_id: 'Search antibiotic class...',
    search_infectious_syndrome_id: 'Search infectious syndrome...',
    search_counterfactual_id: 'Search counterfactual...',
    search_multiple_myeloma_treatment_id: 'Search multiple myeloma treatment...',
    search_education_id: 'Search education...',
    [`search_${VEHSS_CONDITION_ID_KEY}`]: 'Search eye condition...',
    [`search_${VACCINE_STATUS_ID_KEY}`]: 'Search vaccine status...',
    [`search_${VACCINATION_STATUS_ID_KEY}`]: 'Search vaccination status...',
    [`search_${SEROTYPE_ID_KEY}`]: 'Search serotype...',
    [`search_${INFECTION_TYPE_ID_KEY}`]: 'Search infection type...',
    [`search_${CASE_HOSPITALIZATION_ID_KEY}`]: 'Search case hospitalization...',
    [`search_${CASE_OUTCOME_ID_KEY}`]: 'Search case outcome...',
    [`search_${CASE_SEVERITY_ID_KEY}`]: 'Search case severity...',
    [`search_${YEAR_START_KEY}`]: 'Search year start...',
    [`search_${WOLBACHIA_INCLUDED_ID_KEY}`]: 'Search wolbachia...',
    [`search_${PAYER_ID_KEY}`]: 'Search payer...',
    [`search_${TOC_ID_KEY}`]: 'Search type of care...',

    // Refinements
    refinement_condition: 'Condition',
    refinement_condition_type: 'Type',
    refinement_measure_id: 'Measure',
    refinement_metric_id: 'Unit',
    refinement_gender_id: 'Sex',
    refinement_age_group_id: 'Ages',
    refinement_cause_id: 'Cause',
    refinement_location_id: 'Locations',
    refinement_forecast_scenario_id: 'Forecast Scenario',
    refinement_round_id: 'Data Suite',
    refinement_year: 'Years',
    refinement_map_detail_level_id: 'Map Detail',
    refinement_treemap_detail_level_id: 'Detail',
    refinement_map_type_id: 'Map Type',
    refinement_comorbid_cause_id: 'Comorbid Condition',
    refinement_month_count: 'Duration Of Projection (Months)',
    refinement_sequela_id: 'Sequelae',
    refinement_rei_id: 'Risk',
    refinement_covariate_id: 'Covariate',
    refinement_sdoh_id: 'SDoH',
    refinement_risk_attribution: 'Risk Burden',
    refinement_risk_prevalence: 'Risk Prevalence',
    refinement_etiology_records: 'Etiology',
    refinement_impairment_records: 'Impairment',
    refinement_injury_records: 'Injury',
    refinement_sev_records: 'SEV',
    refinement_risk_exposure_records: 'Risk',
    refinement_risk_exposure_id: 'Exposure',
    refinement_race_id: 'Race',
    refinement_facility_id: 'Facility',
    refinement_industry_id: 'Industry',
    refinement_response_id: 'Response',
    refinement_ethnicity_id: 'Ethnicity',
    refinement_indicator_id: 'Indicator',
    refinement_employment_cohort_id: 'Employment Cohort',
    refinement_employment_status_id: 'Employment Status',
    refinement_portal_organization_id: 'Organization',
    refinement_pathogen_id: 'Pathogen',
    refinement_antibiotic_class_id: 'Antibiotic Class',
    refinement_infectious_syndrome_id: 'Infectious Syndrome',
    refinement_counterfactual_id: 'Counterfactual',
    refinement_multiple_myeloma_treatment_id: 'Multiple myeloma treatment',
    refinement_education_id: 'Education',
    [`refinement_${VEHSS_CONDITION_ID_KEY}`]: 'Eye Condition',
    [`refinement_${VACCINE_STATUS_ID_KEY}`]: 'Vaccine Status',
    [`refinement_${WOLBACHIA_INCLUDED_ID_KEY}`]: 'Wolbachia',
    [`refinement_${PAYER_ID_KEY}`]: 'Payer',
    [`refinement_${TOC_ID_KEY}`]: 'Type Of Care',
    [`refinement_${VACCINATION_STATUS_ID_KEY}`]: 'Vaccination Status',
    [`refinement_${SEROTYPE_ID_KEY}`]: 'Serotype',
    [`refinement_${INFECTION_TYPE_ID_KEY}`]: 'Infection Type',
    [`refinement_${CASE_HOSPITALIZATION_ID_KEY}`]: 'Case Hospitalization',
    [`refinement_${CASE_OUTCOME_ID_KEY}`]: 'Case Outcome',
    [`refinement_${CASE_SEVERITY_ID_KEY}`]: 'Case Severity',
    [`refinement_${YEAR_START_KEY}`]: 'Year Start',

    smart_option_select_all_locations_102: 'Select All U.S. Locations',
    smart_option_select_all_countries: 'Select All Countries',
    smart_option_select_all_countries_4: 'Select All Southeast Asia, East Asia, and Oceania',
    smart_option_select_all_countries_31:
        'Select All Central Europe, Eastern Europe, and Central Asia',
    smart_option_select_all_countries_64: 'Select All High Income',
    smart_option_select_all_countries_103: 'Select All Latin America and Caribbean',
    smart_option_select_all_countries_137: 'Select All North Africa and Middle East',
    smart_option_select_all_countries_158: 'Select All South Asia',
    smart_option_select_all_countries_166: 'Select All Sub-Saharan Africa',

    smart_option_select_all_countries_44591: 'Select All World Bank Income Levels',
    smart_option_select_all_countries_479: 'Select All WHO Regions',
    smart_option_select_all_countries_478: 'Select All World Bank Regions',
    smart_option_select_all_countries_44587: 'Select All African Union',
    smart_option_select_all_countries_4743: 'Select All European Union and UK',

    smart_option_select_all_countries_44634: 'Select All High-Middle SDI Countries',
    smart_option_select_all_countries_44635: 'Select All High SDI Countries',
    smart_option_select_all_countries_44636: 'Select All Low-Middle SDI Countries',
    smart_option_select_all_countries_44637: 'Select All Low SDI Countries',
    smart_option_select_all_countries_44639: 'Select All Middle SDI Countries',
    smart_option_select_all_countries_44584: 'Select All OECD Countries',

    smart_option_select_all_5_year_age: 'Select All 5 Year Age Bands',
    smart_option_select_whole_year_age: 'Select Whole Age Range',
    smart_option_select_pediatric_focus: 'Select Pediatric Focus',
    smart_option_select_elderly_focus: 'Select Elderly Focus',
    smart_option_select_reproductive_age_female: 'Select Reproductive Age (Female)',
    smart_option_select_life_course: 'Select Life Course',
    smart_option_select_adult_focus: 'Select Adult Focus',
    smart_option_select_5_yr_age_bands_20_plus: 'Select 5 Year Age Bands 20+',

    smart_option_select_male_and_female: 'Select Male and Female Only',

    smart_option_select_individual_years: 'Select Individual Years',
    smart_option_select_years_range: 'Select a Range',
    smart_option_select_1990s: 'Select 1990s',
    smart_option_select_2000s: 'Select 2000s',
    smart_option_select_2010s: 'Select 2010s',
    smart_option_select_recent_year: 'Recent Year',
    smart_option_select_2020s: 'Select 2020s',
    smart_option_select_2030s: 'Select 2030s',
    smart_option_select_historic_trends: 'Historic Trends',

    smart_option_select_individual_days: 'Select Individual Days',
    smart_option_select_days_range: 'Select a Range',

    data_explorer_select_more: 'Select More...',

    removed_data_collection: 'Removed Data Collection',

    /**
     * Bulk Invite Popup
     */
    bulk_invite_modal_title: 'Bulk Invite (CSV)',
    bulk_invite_modal_description: (
        <>
            <p>
                Invite multiple accounts at once using a CSV file autogenerated by Outlook or
                Google.
            </p>
            <p>
                If creating a new CSV file, title columns as &#39;firstname&#39;,
                &#39;lastname&#39;, &#39;email&#39;, and &#39;title&#39;. The email field is
                required.
            </p>
        </>
    ),
    bulk_invite_modal_file: 'File',
    bulk_invite_modal_successful_upload:
        'File successfully uploaded. If you want to upload a new file click the button below.',
    bulk_invite_modal_overwrite_warning: 'Note: This action will overwrite old file.',
    bulk_invite_modal_upload_new: 'Upload a new file',
    bulk_invite_modal_choose_file: 'Choose File',
    bulk_invite_modal_invite: 'Invite',
    bulk_invite_modal_confirm_inviting: 'Please confirm inviting:',
    bulk_invite_successfully_created_accounts: '%amount% account(s) were created successfully',

    tooltip_edit: 'Edit',
    tooltip_delete: 'Delete',
    tooltip_log_in_as: 'Log in As',
    tooltip_resend_code: 'Resend Code',
    tooltip_resend_invite: 'Resend Invite',
    tooltip_cancel: 'Cancel',
    tooltip_download: 'Download',
    tooltip_share: 'Share',
    tooltip_demote: 'Demote',
    tooltip_promote: 'Promote',
    tooltip_settings: 'Settings',
    tooltip_add_to_list: 'Add To List',
    tooltip_open_link: 'Open Link',
    tooltip_copy: 'Copy To Clipboard',
    tooltip_download_csv: 'Download CSV',

    map_chart_top_list_title: 'Top locations',

    // Change Password Tab
    change_password_success_message:
        'Your password has been successfully changed. Please log in with your new password',
};
